import type { QueryClient } from '@tanstack/react-query';

/**
 * Every cache key that holds Campaign/Post data, invalidated together.
 *
 * Deleting, restoring or purging content touches far more than the list you're looking at: the same
 * post appears in the campaign detail, All Posts, the Calendar, the Trash, the dashboard tiles and
 * its own composer entry. Each call site used to spell this list out by hand, and they had already
 * drifted apart — deleting a campaign from the list view cleared the Calendar while deleting the
 * same campaign from its detail page did not, so its posts stayed on the Calendar until the 30s
 * staleTime lapsed. One helper, so a new call site can't miss a key.
 *
 * `['post', tenantKey, id]` entries are dropped rather than refreshed: the row may no longer be
 * readable at all (every single-post read filters the Trash and 404s), so a cached copy isn't stale
 * data to refresh — it's a phantom the composer could otherwise open as an editable post.
 * INACTIVE entries only, though: tearing out the entry a mounted composer is reading would remount
 * the editor mid-edit and lose whatever the user had typed (publishing from the posts list while a
 * composer is open does exactly that). A live one is invalidated instead, so it refetches in the
 * background and surfaces the 404 through the composer's normal error path.
 */
export function invalidateContentQueries(qc: QueryClient, tenantKey: string): void {
  // The dashboard has no key of its own — its pipeline tiles read ['posts', tenantKey].
  for (const key of ['campaigns', 'campaign', 'posts', 'calendar', 'trash']) {
    void qc.invalidateQueries({ queryKey: [key, tenantKey] });
  }
  qc.removeQueries({ queryKey: ['post', tenantKey], type: 'inactive' });
  void qc.invalidateQueries({ queryKey: ['post', tenantKey], type: 'active' });
}
